import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import type { DialogProps, SelectChangeEvent, SelectProps } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  ACTIVE_TEAM_ACCESS_POLL_MS,
  resolveActiveTeamAccessResult,
  useGetTeamPreference,
  useGetTeams,
  usePollActiveTeamAccess,
  getActiveTeamAccessFromTeamsList,
  getActiveTeamAccessLostCopyKey,
  isActiveTeamAccessLost,
} from "../../../../api/teams";
import { DialogHeader } from "../../../../shared/styles";
import type { RootState } from "../../../../store/store";
import {
  getActiveTeamId,
  getLastKnownActiveTeamName,
  setLastKnownActiveTeamName,
  setTeamAccessLost,
  switchActiveTeamAndNavigateToProfileList,
} from "../../../../utils/active-team/active-team";
import { ALL_TEAMS_LIST_QUERY } from "../teams/shared/team.constants";
import { getSelectableTeams } from "../teams/shared/team.utils";

const EMPTY_TEAM_VALUE = "";

interface TeamAccessLostModalProps {
  enabled: boolean;
}

const blockDismiss: NonNullable<DialogProps["onClose"]> = (
  _event,
  reason
) => {
  if (reason === "backdropClick" || reason === "escapeKeyDown") {
    return;
  }
};

const TeamAccessLostModal = ({
  enabled,
}: TeamAccessLostModalProps): JSX.Element | null => {
  const { t: translate } = useTranslation(["settings"]);
  const [selectedTeamId, setSelectedTeamId] = useState(EMPTY_TEAM_VALUE);
  const [excludedTeamId, setExcludedTeamId] = useState(EMPTY_TEAM_VALUE);
  const [lostTeamName, setLostTeamName] = useState("");
  const permissionsTeamId = useSelector(
    (state: RootState) => state.permissions.teamId
  );
  const sessionTeamId = getActiveTeamId();
  const pollTeamId = sessionTeamId ?? permissionsTeamId ?? "";
  const [trackedTeamId, setTrackedTeamId] = useState(pollTeamId);

  useEffect(() => {
    if (sessionTeamId) {
      setTrackedTeamId(sessionTeamId);
    }
  }, [sessionTeamId]);

  const pollEnabled = enabled && Boolean(pollTeamId);
  const pollIntervalMs =
    process.env.NODE_ENV === "test" ? false : ACTIVE_TEAM_ACCESS_POLL_MS;

  const {
    data: pollData,
    error: pollError,
    isError: isPollError,
  } = usePollActiveTeamAccess(pollTeamId, {
    enabled: pollEnabled,
    pollIntervalMs,
  });

  const pollAccessResult = useMemo(
    () => resolveActiveTeamAccessResult(pollData, isPollError ? pollError : undefined),
    [pollData, isPollError, pollError]
  );

  const { data: teamPreferenceData } = useGetTeamPreference({ enabled });

  const { data: teamsData, isLoading: isTeamsLoading } = useGetTeams(
    ALL_TEAMS_LIST_QUERY,
    {
      enabled,
      refetchInterval: pollEnabled ? pollIntervalMs : false,
    }
  );

  const teamsListAccessResult = useMemo(
    () =>
      getActiveTeamAccessFromTeamsList(
        trackedTeamId,
        teamsData?.teams,
        !isTeamsLoading && Boolean(teamsData),
        teamsData?.apiFailed ?? false
      ),
    [isTeamsLoading, teamsData, trackedTeamId]
  );

  const accessResult = useMemo((): typeof pollAccessResult => {
    if (isActiveTeamAccessLost(pollAccessResult)) {
      return pollAccessResult;
    }

    return teamsListAccessResult ?? pollAccessResult;
  }, [pollAccessResult, teamsListAccessResult]);

  const isLost = isActiveTeamAccessLost(accessResult);

  const teams = useMemo(
    () => getSelectableTeams(teamsData?.teams ?? [], excludedTeamId),
    [excludedTeamId, teamsData?.teams]
  );

  const copyKey = getActiveTeamAccessLostCopyKey(accessResult?.kind);
  const selectTeamPlaceholder = translate("userManagement.editUser.selectTeam");
  const lostTeamId = excludedTeamId || trackedTeamId || pollTeamId;

  const resolvedTeamName = useMemo((): string => {
    const cachedName = getLastKnownActiveTeamName(lostTeamId);
    if (cachedName) {
      return cachedName;
    }

    const teamsListName =
      teamsData?.teams?.find((team) => team.id === lostTeamId)?.name ?? "";
    if (teamsListName) {
      return teamsListName;
    }

    if (
      teamPreferenceData?.teamId === lostTeamId &&
      teamPreferenceData.teamName
    ) {
      return teamPreferenceData.teamName;
    }

    if (pollAccessResult?.kind === "ok" && pollAccessResult.teamName) {
      return pollAccessResult.teamName;
    }

    return "";
  }, [
    lostTeamId,
    pollAccessResult,
    teamPreferenceData?.teamId,
    teamPreferenceData?.teamName,
    teamsData?.teams,
  ]);

  const teamName = lostTeamName || resolvedTeamName;

  const wasLostRef = useRef(false);

  useEffect(() => {
    if (!teamsData?.teams?.length) {
      return;
    }

    const teamIds = new Set(
      [trackedTeamId, pollTeamId, permissionsTeamId].filter(Boolean)
    );

    teamIds.forEach((teamId) => {
      const activeTeam = teamsData.teams.find((team) => team.id === teamId);
      if (activeTeam?.name) {
        setLastKnownActiveTeamName(activeTeam.name, teamId);
      }
    });
  }, [permissionsTeamId, pollTeamId, teamsData?.teams, trackedTeamId]);

  useEffect(() => {
    setTeamAccessLost(isLost);
    return () => {
      setTeamAccessLost(false);
    };
  }, [isLost]);

  useEffect(() => {
    if (!isLost) {
      setExcludedTeamId(EMPTY_TEAM_VALUE);
      setSelectedTeamId(EMPTY_TEAM_VALUE);
      setLostTeamName("");
      wasLostRef.current = false;
      return;
    }

    setExcludedTeamId((current) => current || lostTeamId);
    setLostTeamName((current) => current || resolvedTeamName);

    if (!wasLostRef.current) {
      setSelectedTeamId(EMPTY_TEAM_VALUE);
    }

    wasLostRef.current = true;
  }, [isLost, lostTeamId, resolvedTeamName]);

  const renderTeamValue = useCallback<NonNullable<SelectProps<string>["renderValue"]>>(
    (selected) => {
      if (!selected) {
        return (
          <Box component="span" sx={{ color: "text.secondary" }}>
            {selectTeamPlaceholder}
          </Box>
        );
      }

      const selectedTeam = teams.find((team) => team.id === selected);
      return selectedTeam?.name ?? selected;
    },
    [selectTeamPlaceholder, teams]
  );

  const handleTeamChange = useCallback(
    (event: SelectChangeEvent<string>): void => {
      const nextTeamId = event.target.value;
      if (!nextTeamId) {
        return;
      }

      setSelectedTeamId(nextTeamId);
      switchActiveTeamAndNavigateToProfileList(nextTeamId);
    },
    []
  );

  if (!isLost) {
    return null;
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown
      onClose={blockDismiss}
      role="alertdialog"
      aria-label={String(translate(`${copyKey}.title`))}
      aria-labelledby="team-access-lost-title"
      aria-describedby="team-access-lost-message"
    >
      <DialogHeader id="team-access-lost-title" className="dialog-header">
        {String(translate(`${copyKey}.title`))}
      </DialogHeader>
      <DialogContent>
        <Typography id="team-access-lost-message" sx={{ mb: 2 }}>
          {String(translate(`${copyKey}.message`, { teamName }))}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            id="team-access-lost-select-label"
            sx={{ minWidth: 110, fontWeight: 500 }}
          >
            {selectTeamPlaceholder}:
          </Typography>
          <FormControl size="small" disabled={isTeamsLoading} sx={{ minWidth: 280 }}>
            <Select
              value={selectedTeamId}
              displayEmpty
              onChange={handleTeamChange}
              renderValue={renderTeamValue}
              SelectDisplayProps={{
                "aria-labelledby": "team-access-lost-select-label",
              }}
            >
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TeamAccessLostModal;
